/**
 * Runtime config client — pulls `/api/config` on boot, mirrors it into a
 * tiny zustand store, persists the last good payload to localStorage so the
 * app can render an accurate-ish state on cold load even when the helper
 * is unreachable, and re-polls every 30 s to pick up kill-switch flips.
 *
 *   /api/config response shape (mirrors src/server/routes/runtimeConfig.ts):
 *
 *     {
 *       service: { enabled, notice: { title, message, learnMoreUrl } },
 *       app:     'android-device-hub',
 *       version: string,
 *       loadedAt: number,
 *       capabilities: string[]
 *     }
 *
 * Fallback ladder when the fetch fails:
 *
 *   1. Last value stored in `localStorage[STORAGE_KEY]` (from a previous
 *      successful poll). Lets a returning user reach the maintenance page
 *      offline if ops flipped the switch before the SPA cached fresh data.
 *
 *   2. Optimistic default `{ service.enabled: true }`. We prefer "let the
 *      user try" over "show a maintenance page". If the helper really is
 *      down, the first business API request will fail and the existing
 *      error UI takes over.
 *
 * Why zustand instead of just react-query: the value is read from
 * non-React surfaces too (e.g. the SW update toast, the kill-switch
 * boundary). Keeping it in zustand lets imperative callers
 * `useRuntimeConfig.getState()` without a context hook.
 */
import { create } from 'zustand';
import { hasActiveServiceWorker, resetServiceWorkerAndCaches } from './swReset';
import { API_CREDENTIALS, withApiBase } from './apiBase';

const STORAGE_KEY = 'adh.runtimeConfig.v1';
/**
 * Key for the helper-restart detector's baseline (the previously-seen
 * `loadedAt` value). MUST be a synchronous storage so the value is
 * persisted **before** {@link window.location.reload} fires.
 *
 * History: previously stored under `prefs.global.runtimeConfig.lastSeenLoadedAt`
 * via `useMyPrefsStore.updateGlobal(...)`, but that path debounces the PUT
 * for 300ms before hitting the server, while {@link resetServiceWorkerAndCaches}
 * + `location.reload()` complete in tens of milliseconds. The result: every
 * helper-restart-detected reload arrived at the server with the OLD baseline
 * still on disk; the next boot would re-detect the same restart and reload
 * again, looping until a debounced PUT happened to win the race against
 * the reload (which the user perceived as "the page reloads itself a few
 * times after I start the helper"). `localStorage` writes are synchronous,
 * so the new baseline is on disk before the reload trampoline runs.
 *
 * The old prefs field is intentionally left in place server-side: SPA's
 * deep-merge ignores unknown reads, the schema stays backwards-compatible
 * with older SPA bundles, and there's no migration cost — the old value
 * just becomes dead.
 */
const STORAGE_KEY_LAST_SEEN_LOADED_AT = 'adh.lastSeenLoadedAt.v1';
const POLL_INTERVAL_MS = 30_000;

function readLastSeenLoadedAt(): number {
  if (typeof window === 'undefined' || !window.localStorage) return 0;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_LAST_SEEN_LOADED_AT);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastSeenLoadedAt(ts: number): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY_LAST_SEEN_LOADED_AT, String(ts));
  } catch {
    // QuotaExceeded / private mode — best-effort. The worst case if we
    // really can't persist is exactly the bug we're fixing (loop on
    // restart), so swallow and let the user hard-refresh manually.
  }
}

export interface ServiceNotice {
  title: string;
  message: string;
  learnMoreUrl: string | null;
}

export interface ServiceState {
  enabled: boolean;
  notice: ServiceNotice;
}

export interface RuntimeConfig {
  service: ServiceState;
  app: string;
  version: string;
  loadedAt: number;
  capabilities: string[];
}

/**
 * Categorises the last fetch failure so callers (ServiceGate) can pick
 * which screen to render:
 *
 *   - 'network' : we never received an HTTP response (helper isn't running,
 *                 DNS failure, browser blocked the request, etc).
 *                 → ServiceGate redirects ADH-required routes to
 *                   `/local-service` (which has the 启动服务 button).
 *   - 'http'    : the server responded with a non-2xx status (we got a 503,
 *                 502, 500, …). Helper is reachable but unhealthy.
 *                 → keep showing the last good config (MaintenanceScreen if
 *                   that was disabled, main UI otherwise) since the API
 *                   might recover on the next poll.
 *   - 'parse'   : 2xx but the body was malformed JSON. Treat like 'http'.
 *
 * `null` means the latest fetch succeeded.
 */
export type LastErrorKind = 'network' | 'http' | 'parse';

export interface LastError {
  kind: LastErrorKind;
  message: string;
  /** Wall-clock timestamp of when this error happened. */
  at: number;
}

interface RuntimeConfigStore {
  /** Current effective config (server / cache / defaults). */
  config: RuntimeConfig;
  /** True until the first fetch settles (success OR failure). */
  initialised: boolean;
  /** Most recent fetch error (categorised), or null if the last poll succeeded. */
  lastError: LastError | null;
  /** Source of {@link config}: 'server' (live), 'cache' (LS), 'default'. */
  source: 'server' | 'cache' | 'default';
  /** Total successful polls since the last failure (resets on error). */
  consecutiveSuccess: number;
  /** Total failed polls since the last success (resets on success). */
  consecutiveFailure: number;
  /**
   * Set to `true` between detecting "helper restarted" and the actual
   * `location.reload()` call. The window is tiny (one microtask plus
   * the time to delete the CacheStorage buckets) but enough for React
   * to render one frame; `<ServiceGate>` reads this and shows the
   * "正在加载最新版本…" placeholder so the user sees a clean
   * "本地服务已更新" transition instead of a momentary main-UI flash.
   */
  pendingHelperRestartReload: boolean;
  fetchOnce: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  /**
   * Switch to fast polling (used by `/local-service` & Home while the user
   * has just clicked "启动服务" / "一键启动" — we want to detect the helper
   * coming up within ~1s, not after 30s). Idempotent; the next call to
   * {@link useRuntimeConfig.startPolling} or successful poll restores
   * the standard interval.
   */
  enterFastPoll: (totalMs?: number) => void;
}

const OPTIMISTIC_DEFAULT: RuntimeConfig = {
  service: {
    enabled: true,
    notice: { title: '服务公告', message: '', learnMoreUrl: null },
  },
  app: 'android-device-hub',
  version: '0.0.0-unknown',
  loadedAt: 0,
  capabilities: [],
};

function readCache(): RuntimeConfig | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RuntimeConfig>;
    return normalise(parsed);
  } catch {
    return null;
  }
}

function writeCache(c: RuntimeConfig): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    // Quota / private-mode write failures are non-fatal — we just can't
    // give the user offline kill-switch awareness next time.
  }
}

/**
 * Coerce a partial / external payload into our schema, filling defaults on
 * a per-field basis. We do this both for cached values (older SPA versions
 * may have stored a different shape) and for server responses (forward
 * compatibility if the server ever ships extra unknown fields).
 */
function normalise(raw: Partial<RuntimeConfig> | null | undefined): RuntimeConfig {
  const d = OPTIMISTIC_DEFAULT;
  const s = raw?.service ?? d.service;
  const sn = s?.notice ?? d.service.notice;
  return {
    service: {
      enabled: typeof s?.enabled === 'boolean' ? s.enabled : d.service.enabled,
      notice: {
        title: typeof sn?.title === 'string' ? sn.title : d.service.notice.title,
        message: typeof sn?.message === 'string' ? sn.message : d.service.notice.message,
        learnMoreUrl:
          typeof sn?.learnMoreUrl === 'string'
            ? sn.learnMoreUrl
            : sn?.learnMoreUrl === null
              ? null
              : d.service.notice.learnMoreUrl,
      },
    },
    app: typeof raw?.app === 'string' ? raw.app : d.app,
    version: typeof raw?.version === 'string' ? raw.version : d.version,
    loadedAt: typeof raw?.loadedAt === 'number' ? raw.loadedAt : d.loadedAt,
    capabilities: Array.isArray(raw?.capabilities)
      ? raw.capabilities.filter((x): x is string => typeof x === 'string')
      : d.capabilities,
  };
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let fastPollDeadline = 0;
const FAST_POLL_INTERVAL_MS = 1_000;

function currentPollInterval(): number {
  return Date.now() < fastPollDeadline ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
}

/**
 * Classify a thrown error from {@link fetch}. The browser is intentionally
 * vague — `fetch` rejects with a `TypeError` whose message varies per
 * browser ("Failed to fetch" on Chrome, "NetworkError when attempting…"
 * on Firefox, "Load failed" on Safari) for the network-down case, and
 * with the same `TypeError` shape when the request is aborted by a
 * service worker / CSP. We can't enumerate the messages, so any
 * `TypeError` at this layer is treated as "network".
 *
 * Non-2xx HTTP responses come through the manually-thrown `HTTP NNN`
 * branch in {@link fetchOnce} and are classified 'http'.
 *
 * Anything else (JSON parse, unexpected shape) → 'parse'.
 */
function classifyError(err: unknown): LastError {
  const at = Date.now();
  if (err instanceof TypeError) {
    return { kind: 'network', message: err.message || 'network error', at };
  }
  if (err instanceof Error) {
    if (/^HTTP \d+/.test(err.message)) {
      return { kind: 'http', message: err.message, at };
    }
    return { kind: 'parse', message: err.message, at };
  }
  return { kind: 'network', message: String(err), at };
}

export const useRuntimeConfig = create<RuntimeConfigStore>((set, get) => ({
  config: readCache() ?? OPTIMISTIC_DEFAULT,
  initialised: false,
  lastError: null,
  source: readCache() ? 'cache' : 'default',
  consecutiveSuccess: 0,
  consecutiveFailure: 0,
  pendingHelperRestartReload: false,

  async fetchOnce() {
    try {
      // `withApiBase` rewrites to `http://127.0.0.1:10987/api/config` when
      // the SPA is served from a public hub origin (e.g. hub.example.com);
      // returns the relative path verbatim in the same-origin PWA mode.
      // `API_CREDENTIALS` flips to `'omit'` in the cross-origin case so
      // we don't collide with `Allow-Origin: *` on the server side.
      const res = await fetch(withApiBase('/api/config'), {
        method: 'GET',
        credentials: API_CREDENTIALS,
        headers: { accept: 'application/json' },
        // Pair with the server's `Cache-Control: no-store` and the workbox
        // `NetworkOnly` rule. Belt + suspenders so a misconfigured proxy
        // doesn't poison the kill-switch state.
        cache: 'no-store',
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = (await res.json()) as Partial<RuntimeConfig>;
      const next = normalise(json);
      writeCache(next);

      // Detect helper-restart-while-stale-SW-was-serving and reset/reload
      // ONCE so the user never has to know about `?clear-sw=1`. Three AND
      // conditions are required to avoid false positives:
      //
      //   1. `seenNum > 0` — we have an existing baseline from a previous
      //      successful poll. Without this every very-first page load
      //      would self-reload immediately, which is awful UX.
      //   2. `seenNum !== next.loadedAt` — the helper *actually* came up
      //      under a different boot timestamp. The server picks a fresh
      //      `BOOT_TIMESTAMP` per module init, so the same process serving
      //      multiple polls yields identical values here.
      //   3. `hasActiveServiceWorker()` — there's a SW controlling the
      //      page. In `vite dev` mode SW registration is opt-in and
      //      almost never present; we don't want `nodemon`-style helper
      //      restarts to thrash the dev page. In prod-PWA mode the SW
      //      IS the reason for the stale bundle so we only auto-reset
      //      when one is actually in the picture.
      //
      // The localStorage write happens BEFORE we even consider the reload
      // path so the post-reload boot is guaranteed to see `next.loadedAt`
      // as its baseline. localStorage is synchronous — the value is on
      // disk before this function returns, and certainly before the
      // reload trampoline below runs. (We learned this the hard way:
      // an earlier version stored the baseline through the prefs store,
      // which debounces the PUT for 300ms; the reload always beat the
      // PUT, the next boot saw the OLD baseline, and the page looped
      // through several reloads until a PUT eventually won the race.)
      const seenNum = readLastSeenLoadedAt();
      writeLastSeenLoadedAt(next.loadedAt);
      if (
        seenNum > 0 &&
        next.loadedAt > 0 &&
        seenNum !== next.loadedAt &&
        hasActiveServiceWorker()
      ) {
        // Mark the store so <ServiceGate> swaps to a calm "正在加载最新
        // 版本…" placeholder for the ~50ms between this point and the
        // actual reload. Without this React would briefly try to render
        // the main UI against possibly-mismatched server / cache state.
        set({ pendingHelperRestartReload: true });
        // eslint-disable-next-line no-console
        console.info('[adh] helper restart detected (loadedAt changed); resetting SW + reloading');
        await resetServiceWorkerAndCaches();
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
        // Bail BEFORE the normal set() so a stray render between the
        // reload trigger and the actual nav doesn't downgrade us back
        // to a "healthy" state.
        return;
      }

      // Successful poll → leave fast-poll mode early. The next setInterval
      // tick will pick up the slower cadence automatically.
      fastPollDeadline = 0;
      set((prev) => ({
        config: next,
        initialised: true,
        lastError: null,
        source: 'server',
        consecutiveSuccess: prev.consecutiveSuccess + 1,
        consecutiveFailure: 0,
      }));
    } catch (err) {
      set((prev) => ({
        initialised: true,
        lastError: classifyError(err),
        // Don't downgrade source when we already have a server-fresh value.
        source: prev.source === 'server' ? 'server' : (readCache() ? 'cache' : 'default'),
        consecutiveSuccess: 0,
        consecutiveFailure: prev.consecutiveFailure + 1,
      }));
    }
  },

  startPolling() {
    if (pollTimer) return;
    void get().fetchOnce();
    const tick = (): void => {
      void get().fetchOnce();
      // Re-arm with the *current* interval, which lets enterFastPoll()
      // shrink the cadence on the fly without us needing to clear and
      // reset the timer from outside.
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = setInterval(tick, currentPollInterval());
    };
    pollTimer = setInterval(tick, currentPollInterval());
  },

  stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    fastPollDeadline = 0;
  },

  enterFastPoll(totalMs = 30_000) {
    // Bump the deadline forward; the next setInterval tick will read
    // currentPollInterval() and resize itself to 1s. We also kick a
    // fresh fetch immediately so the user sees feedback within ~1s.
    fastPollDeadline = Math.max(fastPollDeadline, Date.now() + totalMs);
    void get().fetchOnce();
    if (pollTimer) {
      clearInterval(pollTimer);
      const tick = (): void => {
        void get().fetchOnce();
        if (pollTimer) clearInterval(pollTimer);
        pollTimer = setInterval(tick, currentPollInterval());
      };
      pollTimer = setInterval(tick, currentPollInterval());
    }
  },
}));

/** Convenient selector hooks. */
export function useServiceState(): ServiceState {
  return useRuntimeConfig((s) => s.config.service);
}

export function useHelperVersion(): string {
  return useRuntimeConfig((s) => s.config.version);
}
